<template>
  <div class="flex min-h-screen flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img
        class="mx-auto h-12 w-auto"
        src="../../assets/imgs/logo/logo-multiple-no-padding.svg"
        alt="Workflow"
      />
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
        <form @submit.prevent="signUp" class="space-y-6" action="#" method="POST">
          <div v-if="showEmailAndCodeField">
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.sign_up.email") }}
            </label>
            <div class="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autocomplete="email"
                required=""
                v-model="email"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
            </div>
          </div>
          <div v-if="showEmailAndCodeField">
            <label for="passwordTemp" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.sign_up.temporary_password") }}
            </label>
            <div class="mt-1 relative">
              <input
                id="passwordTemp"
                name="passwordTemp"
                :type="showTempPassword ? 'text' : 'password'"
                required=""
                v-model="passwordTemp"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 pl-3 pr-9 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
              <EyeIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showTempPassword = true"
                v-if="!showTempPassword"
              />
              <EyeSlashIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showTempPassword = false"
                v-if="showTempPassword"
              />
            </div>
          </div>
          <div>
            <label for="passwordNewFirst" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.sign_up.new_password") }}
            </label>
            <div class="mt-1 relative">
              <input
                id="passwordNewFirst"
                name="passwordNewFirst"
                :type="showNewPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required=""
                v-model="passwordNewFirst"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 pl-3 pr-9 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
              <EyeIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showNewPassword = true"
                v-if="!showNewPassword"
              />
              <EyeSlashIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showNewPassword = false"
                v-if="showNewPassword"
              />
            </div>
          </div>

          <div>
            <label for="passwordNewSecond" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.sign_up.repeat_new_password") }}
            </label>
            <div class="mt-1 relative">
              <input
                id="passwordNewSecond"
                name="passwordNewSecond"
                :type="showNewSecondPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required=""
                v-model="passwordNewSecond"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 pl-3 pr-9 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
              <EyeIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showNewSecondPassword = true"
                v-if="!showNewSecondPassword"
              />
              <EyeSlashIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showNewSecondPassword = false"
                v-if="showNewSecondPassword"
              />
            </div>
          </div>

          <div class="rounded-md bg-red-50 p-4" v-show="invalidInputAlert">
            <div class="flex">
              <div class="shrink-0">
                <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">
                  {{ this.errorMessageInvalidInput }}
                </h3>
              </div>
            </div>
          </div>

          <div class="rounded-md bg-yellow-100 p-4" v-show="userExistsAlert">
            <div class="flex">
              <div class="shrink-0">
                <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-yellow-800">Der Benutzer existiert bereits</h3>
                <div class="mt-2 text-sm text-yellow-700">
                  <p>
                    Bitte logge dich über die
                    <u>
                      <router-link to="/log-in">Login-Seite</router-link>
                    </u>
                    ein.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="text-center text-sm">
            {{ $t("authentication.sign_up.terms_conditions_1") }}
            <a href="https://www.oculai.de/agb"
              ><span class="text-yellow-800 font-medium">
                {{ $t("authentication.sign_up.terms_conditions_2") }}
              </span></a
            >
            {{ $t("authentication.sign_up.terms_conditions_3") }}
          </div>
          <div>
            <button
              type="submit"
              :disabled="loading"
              class="focus:outline-none flex w-full justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
            >
              {{ $t("authentication.sign_up.sign_up") }}
            </button>
          </div>
        </form>
        <div class="flex items-center gap-3 text-sm text-gray-400 my-6">
          <div class="border-t flex-1" />
          {{ $t("authentication.login.or") }}
          <div class="border-t flex-1" />
        </div>
        <div class="rounded-md bg-red-50 p-4 mb-6" v-if="socialAlert">
          <div class="flex">
            <div class="shrink-0">
              <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                {{ $t("authentication.sign_up.social_error") }}
              </h3>
            </div>
          </div>
        </div>
        <button
          class="border px-5 py-2 rounded-lg flex gap-3 w-full justify-center mb-6"
          type="button"
          style="font-family: Montserrat-SemiBold, system-ui"
          @click="triggerMicrosoftLogin"
        >
          <img
            alt="microsoft logo"
            src="data:image/svg+xml,%3csvg%20width='21'%20height='21'%20viewBox='0%200%2021%2021'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3crect%20x='11.375'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%23FEBA08'/%3e%3crect%20x='0.875'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%2305A6F0'/%3e%3crect%20x='11.375'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%2380BC06'/%3e%3crect%20x='0.875'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%23F25325'/%3e%3c/svg%3e"
          /><span>{{ $t("authentication.sign_up.sign_up_with_microsoft") }}</span>
        </button>
        <div class="pt-4 text-center text-sm text-yellow-900">
          <router-link to="/log-in" class="font-sm hover:text-yellow-700">
            {{ $t("authentication.sign_up.already_registered") }}
          </router-link>
        </div>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import {
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/vue/24/solid";
import { Auth } from "aws-amplify";
import AuthMixins from "@/mixins/AuthMixins";
import logger from "@/services/logger";
import Footer from "@/views/auth/components/Footer";

export default {
  name: "SignUp",
  components: {
    EyeSlashIcon,
    EyeIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    Footer,
  },
  mixins: [AuthMixins],
  methods: {
    signUp() {
      this.loading = true;
      this.invalidInputAlert = false;
      this.userExistsAlert = false;

      Auth.signIn(this.email, this.passwordTemp)
        .then((user) => {
          if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            if (this.passwordNewFirst === this.passwordNewSecond) {
              return Auth.completeNewPassword(user, this.passwordNewFirst)
                .then(() => {
                  this.$router.push("/log-in");
                })
                .catch((e) => {
                  switch (e.message) {
                    case "Username should be either an email or a phone number.":
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_must_be_mail",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                      break;
                    case "Password does not conform to policy: Password not long enough":
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_password_length",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                      break;
                    case "User is not confirmed.":
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_user_not_confirmed",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                      break;
                    case "Incorrect username or password.":
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_mail_password",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                      break;
                    case "User does not exist.":
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_user_not_existing",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                      break;
                    default:
                      this.errorMessageInvalidInput = this.$t(
                        "authentication.sign_up.error_unknown",
                      );
                      this.invalidInputAlert = !this.invalidInputAlert;
                  }
                });
            } else {
              this.errorMessageInvalidInput = this.$t(
                "authentication.sign_up.error_password_not_equal",
              );
              this.invalidInputAlert = !this.invalidInputAlert;
            }
          } else {
            this.userExistsAlert = !this.userExistsAlert;
          }
        })
        .catch((error) => {
          this.errorMessageInvalidInput = this.$t("authentication.sign_up.error_mail_password");
          this.invalidInputAlert = !this.invalidInputAlert;
          if (error.message !== "Incorrect username or password.") {
            logger.error("Unable to sign up", error);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async triggerMicrosoftLogin() {
      this.loading = true;
      this.errorMessageInvalidInput = "";
      this.userExistsAlert = false;
      this.invalidInputAlert = false;
      this.socialAlert = false;

      try {
        await this.microsoftSignIn(this.$route.query.email);
        this.$router.push("/");
      } catch (error) {
        if (
          error?.message !== "Different email in logged in account" &&
          error !== "No current user" &&
          error?.message !== "user_cancelled: User cancelled the flow." &&
          !error?.message.includes("interaction_in_progress")
        ) {
          logger.error(error);
        }
        this.socialAlert = true;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.email = this.$route.query.email || "";
    this.passwordTemp = atob(this.$route.query.code || "");
    this.showEmailAndCodeField = !this.email || !this.passwordTemp;
  },
  data() {
    return {
      email: "",
      passwordTemp: "",
      passwordNewFirst: "",
      passwordNewSecond: "",
      errorMessageInvalidInput: "",
      userExistsAlert: false,
      invalidInputAlert: false,
      loading: false,
      showTempPassword: false,
      showNewPassword: false,
      showNewSecondPassword: false,
      socialAlert: false,
      showEmailAndCodeField: false,
    };
  },
};
</script>
